import { useEffect, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  BookOpenText,
  CircleDollarSign,
  ClipboardPaste,
  Earth,
  Groceries,
  LogIn,
  MessageCircleMore,
  MessagesSquare,
  ShoppingCart,
  Truck,
  UserPlus,
  UserStar,
  Users,
  Warehouse,
  Wheat,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FullscreenLoading } from "@/components/FullscreenLoading";
import { Farm2GoIcon } from "@/components/Farm2GoIcon";
import {
  generateNonce,
  getLoginUrl,
  getStoredSessionRedirectPath,
} from "../../utils/authentication";
import heroImageUrl from "../../assets/images/hero.jpg";

const TRAINING_MODE_URL = "https://qa.farm2go.wfp.org/";

type PersonaCta =
  | {
      kind: "google-play";
      href: string;
      badgeImageSrc: string;
      labelKey: string;
    }
  | { kind: "login"; labelKey: string }
  | { kind: "icons"; icons: LucideIcon[] };

type PersonaCard = {
  id: string;
  titleKey: string;
  platformKey: string;
  descriptionKey: string;
  icon: LucideIcon;
  cta: PersonaCta;
};

const personaCards: PersonaCard[] = [
  {
    id: "smallholder-farmers",
    titleKey: "login.landing.roles.smallholderFarmers.title",
    platformKey: "login.landing.roles.smallholderFarmers.platform",
    descriptionKey: "login.landing.roles.smallholderFarmers.description",
    icon: Users,
    cta: { kind: "icons", icons: [MessageCircleMore] },
  },
  {
    id: "aggregators",
    titleKey: "login.landing.roles.aggregators.title",
    platformKey: "login.landing.roles.aggregators.platform",
    descriptionKey: "login.landing.roles.aggregators.description",
    icon: Warehouse,
    cta: {
      kind: "google-play",
      href: "https://play.google.com/store/apps/details?id=org.wfp.farm2go",
      badgeImageSrc: "/static/google-play.svg",
      labelKey: "login.landing.googlePlayBadgeAlt",
    },
  },
  {
    id: "buyers",
    titleKey: "login.landing.roles.buyers.title",
    platformKey: "login.landing.roles.buyers.platform",
    descriptionKey: "login.landing.roles.buyers.description",
    icon: ShoppingCart,
    cta: {
      kind: "login",
      labelKey: "login.landing.logIn",
    },
  },
  {
    id: "country-administrators",
    titleKey: "login.landing.roles.countryAdmins.title",
    platformKey: "login.landing.roles.countryAdmins.platform",
    descriptionKey: "login.landing.roles.countryAdmins.description",
    icon: UserStar,
    cta: {
      kind: "login",
      labelKey: "login.landing.logIn",
    },
  },
];

function PersonaCta({
  cta,
  isLoginLoading,
  onLogin,
}: {
  cta: PersonaCta;
  isLoginLoading: boolean;
  onLogin: () => void;
}) {
  const { t } = useTranslation();

  if (cta.kind === "icons") {
    return (
      <div className="flex items-center gap-2" aria-hidden="true">
        {cta.icons.map((Icon, index) => (
          <Icon key={index} className="size-10 rounded-lg p-2 text-blue-600 bg-blue-100" />
        ))}
      </div>
    );
  }

  if (cta.kind === "google-play") {
    return (
      <a
        href={cta.href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex w-fit rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <img
          src={cta.badgeImageSrc}
          alt={t(cta.labelKey)}
          className="h-10 w-auto"
          decoding="async"
        />
      </a>
    );
  }

  return (
    <WFPButton
      type="button"
      size="lg"
      className="w-fit"
      disabled={isLoginLoading}
      onClick={onLogin}
    >
      <LogIn aria-hidden="true" />
      {t(cta.labelKey)}
    </WFPButton>
  );
}

const workflowSteps = [
  {
    id: "register-farmers",
    titleKey: "login.landing.workflow.registerFarmers.title",
    descriptionKey: "login.landing.workflow.registerFarmers.description",
    icon: UserPlus,
  },
  {
    id: "collect-produce",
    titleKey: "login.landing.workflow.collectProduce.title",
    descriptionKey: "login.landing.workflow.collectProduce.description",
    icon: Wheat,
  },
  {
    id: "connect-buyers",
    titleKey: "login.landing.workflow.connectBuyers.title",
    descriptionKey: "login.landing.workflow.connectBuyers.description",
    icon: ShoppingCart,
  },
  {
    id: "delivery-and-payment",
    titleKey: "login.landing.workflow.deliveryAndPayment.title",
    descriptionKey: "login.landing.workflow.deliveryAndPayment.description",
    icon: Truck,
  },
  {
    id: "keep-farmers-informed",
    titleKey: "login.landing.workflow.keepFarmersInformed.title",
    descriptionKey: "login.landing.workflow.keepFarmersInformed.description",
    icon: MessagesSquare,
  },
  {
    id: "knowledge-sharing",
    titleKey: "login.landing.workflow.knowledgeSharing.title",
    descriptionKey: "login.landing.workflow.knowledgeSharing.description",
    icon: BookOpenText,
  },
];

const trustStatRows = [
  [{ key: "login.landing.stats.countries", icon: Earth }],
  [
    { key: "login.landing.stats.farmersRegistered", icon: UserPlus },
    { key: "login.landing.stats.aggregators", icon: Warehouse },
  ],
  [
    { key: "login.landing.stats.produce", icon: Groceries },
    { key: "login.landing.stats.producedValue", icon: CircleDollarSign },
  ],
];

export const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const sessionRedirectPath = getStoredSessionRedirectPath();
  const showTrainingModeLink = process.env.SENTRY_ENVIRONMENT !== "QA";

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsLoading(false);
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const handleLogin = () => {
    setIsLoading(true);
    const nonce = generateNonce();
    localStorage.setItem("nonce", nonce);
    window.open(getLoginUrl(nonce), "_self");
  };

  if (sessionRedirectPath) {
    return (
      <>
        <FullscreenLoading isVisible />
        <Redirect to={sessionRedirectPath} />
      </>
    );
  }

  if (isLoading) {
    return <FullscreenLoading isVisible />;
  }

  return (
    <div className="text-foreground">
      <header className="sticky top-0 z-10 bg-background shadow-md px-10">
        <div className="mx-auto flex w-[min(1120px,calc(100%-3rem))] items-center justify-between gap-4 py-4 w-full max-w-6xl">
          <div>
            <p className="m-0 flex items-center gap-2 text-xl font-bold text-primary">
              <Farm2GoIcon />
              Farm2Go
            </p>
          </div>
          <div className="flex gap-3">
            <WFPButton type="button" disabled={isLoading} onClick={handleLogin}>
              <LogIn aria-hidden="true" />
              {t("login.landing.logIn")}
            </WFPButton>
          </div>
        </div>
      </header>

      <main>
        <section
          className="px-10 relative flex min-h-96 items-center bg-cover bg-center bg-no-repeat py-24 max-[720px]:min-h-112 max-[720px]:py-12"
          style={{ backgroundImage: `url(${heroImageUrl})` }}
        >
          <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
          <div className="relative mx-auto w-full max-w-6xl">
            <div className="flex flex-col gap-8 max-w-2xl">
              <h1 className="text-4xl font-bold text-white md:text-5xl max-w-[66ch]">
                {t("login.landing.hero.title")}
              </h1>
              <p className="text-lg text-white max-w-[66ch]">
                {t("login.landing.hero.description")}
              </p>
            </div>
          </div>
        </section>

        <section className="px-10 py-24 bg-muted">
          <div className="mx-auto w-full max-w-6xl flex flex-col gap-12">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h2 className="text-3xl font-bold">{t("login.landing.roles.title")}</h2>
              {showTrainingModeLink && (
                <WFPButton variant="secondary" asChild>
                  <a href={TRAINING_MODE_URL}>
                    <ClipboardPaste aria-hidden="true" />
                    {t("login.landing.roles.trainingMode")}
                  </a>
                </WFPButton>
              )}
            </div>
            <div className="grid grid-cols-4 gap-5 max-[1100px]:grid-cols-2 max-[720px]:grid-cols-1">
              {personaCards.map(persona => (
                <Card
                  key={persona.id}
                  className="row-span-4 grid grid-rows-subgrid gap-6 shadow-none"
                >
                  <CardHeader className="flex items-center gap-5">
                    <persona.icon
                      className="size-12 rounded-lg p-2.5 text-blue-600 bg-blue-100"
                      aria-hidden="true"
                    />
                    <div className="flex flex-col gap-2">
                      <CardTitle className="text-xl">{t(persona.titleKey)}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="row-span-3 grid grid-rows-subgrid gap-6">
                    <CardDescription>{t(persona.descriptionKey)}</CardDescription>
                    <Badge variant="secondary" className="w-fit">
                      {t(persona.platformKey)}
                    </Badge>
                    <div>
                      <PersonaCta
                        cta={persona.cta}
                        isLoginLoading={isLoading}
                        onLogin={handleLogin}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="px-10 py-24 bg-background">
          <div className="mx-auto w-full max-w-6xl flex flex-col gap-12">
            <h2 className="text-3xl font-bold">{t("login.landing.workflow.title")}</h2>
            <div className="grid grid-cols-3 gap-12 max-[980px]:grid-cols-2 max-[720px]:grid-cols-1">
              {workflowSteps.map(step => (
                <Card key={step.id} className="border-none shadow-none py-0">
                  <CardHeader className="p-0 gap-2.5">
                    <step.icon
                      className={`size-11 rounded-lg p-2.5 text-blue-600 bg-blue-100 mb-1`}
                      aria-hidden="true"
                    />
                    <CardTitle className="text-lg">{t(step.titleKey)}</CardTitle>
                    <CardDescription>{t(step.descriptionKey)}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="px-10 py-24 bg-muted">
          <div className="mx-auto w-full max-w-6xl flex flex-col gap-12">
            <h2 className="text-3xl font-bold">{t("login.landing.stats.title")}</h2>
            <div className="flex flex-col gap-3">
              {trustStatRows.map(row => (
                <div key={row[0].key} className="flex flex-wrap gap-3">
                  {row.map(stat => (
                    <Badge key={stat.key} className="gap-2 px-3.5 py-2 text-base [&>svg]:size-4">
                      <stat.icon aria-hidden="true" />
                      {t(stat.key)}
                    </Badge>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-primary px-10 py-8">
        <div className="mx-auto w-full max-w-6xl">
          <p className="m-0 flex items-center gap-2 text-xl font-bold text-white">
            <Farm2GoIcon className="text-white" />
            Farm2Go
          </p>
        </div>
      </footer>
    </div>
  );
};
